import { create } from 'zustand';
import type { NotificationItem } from '../hooks/use-notifications-api.hook';

/**
 * Global notification state for the topbar bell. Lives outside React so it
 * survives per-page TopNavBar remounts; NOT persisted (a stale unread count is
 * worse than a refetch). Polling is driven by NotificationsProvider.
 */
interface NotificationState {
  items: NotificationItem[];
  unreadCount: number;
  isLoading: boolean;
  /** When a notification last arrived live, and its type — pages that show Ardi's work refresh on it. */
  lastArrival: { at: number; type: string } | null;
  setItems: (items: NotificationItem[]) => void;
  setUnreadCount: (count: number) => void;
  setLoading: (loading: boolean) => void;
  pushIncoming: (item: NotificationItem) => void;
  markAllReadLocal: () => void;
  markReadLocal: (id: string) => void;
  noteArrival: (type: string) => void;
  reset: () => void;
}

const initialState = {
  items: [] as NotificationItem[],
  unreadCount: 0,
  isLoading: false,
  lastArrival: null as { at: number; type: string } | null,
};

export const useNotificationStore = create<NotificationState>((set) => ({
  ...initialState,

  setItems: (items) => set({ items }),

  setUnreadCount: (count) => set({ unreadCount: count }),

  setLoading: (loading) => set({ isLoading: loading }),

  // Real-time arrival: bump the badge and prepend to the list (deduped —
  // reconnects can redeliver), keeping the panel's page size.
  pushIncoming: (item) =>
    set((state) => {
      if (state.items.some((existing) => existing._id === item._id)) return state;
      return {
        unreadCount: state.unreadCount + 1,
        items: [item, ...state.items].slice(0, 20),
      };
    }),

  markAllReadLocal: () => set({ unreadCount: 0 }),

  markReadLocal: (id) =>
    set((state) => {
      const item = state.items.find((i) => i._id === id);
      const wasUnread = item ? (item.read === undefined ? !item.readAt : !item.read) : false;
      return {
        items: state.items.map((i) => (i._id === id ? { ...i, read: true } : i)),
        unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
      };
    }),

  noteArrival: (type) => set({ lastArrival: { at: Date.now(), type } }),

  reset: () => set({ ...initialState }),
}));
